
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { AutomationRule, CreateAutomationRuleData } from '../types/automationTypes';
import { validateAutomationRule } from '../schemas/automationRuleSchema';
import { useUser } from '@/hooks/useUser';

export const useAutomationRules = () => {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useUser();

  // Fetch rules from Supabase
  const fetchRules = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('automation_rules')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching automation rules:', error);
        toast.error('Failed to load automation rules');
        return;
      }

      // Transform the data to match our AutomationRule type
      const transformedRules: AutomationRule[] = (data || []).map(rule => ({
        ...rule,
        validation_errors: Array.isArray(rule.validation_errors) 
          ? rule.validation_errors.map(err => String(err))
          : [],
        trigger: rule.trigger as any,
        actions: rule.actions as any,
        priority: rule.priority as 'high' | 'medium' | 'low', // Type cast to ensure compatibility
      }));

      setRules(transformedRules);
    } catch (error) {
      console.error('Error fetching automation rules:', error);
      toast.error('Failed to load automation rules');
    } finally {
      setIsLoading(false);
    }
  };

  // Set up real-time subscription
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    fetchRules();

    const channel = supabase
      .channel('automation_rules_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automation_rules',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Automation rule change detected:', payload);
          
          switch (payload.eventType) {
            case 'INSERT':
              const newRule = {
                ...payload.new,
                validation_errors: Array.isArray(payload.new.validation_errors) 
                  ? payload.new.validation_errors.map(err => String(err))
                  : [],
                trigger: payload.new.trigger as any,
                actions: payload.new.actions as any,
                priority: payload.new.priority as 'high' | 'medium' | 'low',
              } as AutomationRule;
              setRules(prev => [newRule, ...prev]);
              break;
            case 'UPDATE':
              const updatedRule = {
                ...payload.new,
                validation_errors: Array.isArray(payload.new.validation_errors) 
                  ? payload.new.validation_errors.map(err => String(err))
                  : [],
                trigger: payload.new.trigger as any,
                actions: payload.new.actions as any,
                priority: payload.new.priority as 'high' | 'medium' | 'low',
              } as AutomationRule;
              setRules(prev => prev.map(rule => 
                rule.id === payload.new.id ? updatedRule : rule
              ));
              break;
            case 'DELETE':
              setRules(prev => prev.filter(rule => rule.id !== payload.old.id));
              break;
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const createRule = async (rule: CreateAutomationRuleData) => {
    if (!user) {
      toast.error('You must be logged in to create automation rules');
      return;
    }

    setIsLoading(true);
    try {
      // Validate before creating
      const validation = validateAutomationRule(rule);
      if (!validation.success) {
        toast.error(`Validation failed: ${validation.errors?.join(', ')}`);
        return;
      }

      const ruleData = {
        name: rule.name,
        description: rule.description,
        enabled: rule.enabled,
        trigger: rule.trigger,
        actions: rule.actions,
        priority: rule.priority,
        user_id: user.id,
        trigger_count: 0,
        is_valid: true,
        validation_errors: [],
        last_validated: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('automation_rules')
        .insert([ruleData]);

      if (error) {
        console.error('Error creating automation rule:', error);
        toast.error('Failed to create automation rule');
        return;
      }

      toast.success('Automation rule created successfully');
    } catch (error) {
      console.error('Error creating automation rule:', error);
      toast.error('Failed to create automation rule');
    } finally {
      setIsLoading(false);
    }
  };

  const updateRule = async (id: string, updates: Partial<AutomationRule>) => {
    if (!user) {
      toast.error('You must be logged in to update automation rules');
      return;
    }

    setIsLoading(true);
    try {
      // Get current rule for validation
      const currentRule = rules.find(rule => rule.id === id);
      if (!currentRule) {
        toast.error('Rule not found');
        return;
      }

      const updatedRule = { ...currentRule, ...updates };
      const validation = validateAutomationRule(updatedRule);
      
      const updateData = {
        ...updates,
        is_valid: validation.success,
        validation_errors: validation.errors || [],
        last_validated: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('automation_rules')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating automation rule:', error);
        toast.error('Failed to update automation rule');
        return;
      }

      toast.success('Automation rule updated successfully');
    } catch (error) {
      console.error('Error updating automation rule:', error);
      toast.error('Failed to update automation rule');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRule = async (id: string) => {
    if (!user) {
      toast.error('You must be logged in to delete automation rules');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('automation_rules')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting automation rule:', error);
        toast.error('Failed to delete automation rule');
        return;
      }

      toast.success('Automation rule deleted successfully');
    } catch (error) {
      console.error('Error deleting automation rule:', error);
      toast.error('Failed to delete automation rule');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    rules,
    isLoading,
    createRule,
    updateRule,
    deleteRule,
    refetch: fetchRules,
  };
};
